import React from "react";
import { t } from "../i18n";
import "./MontageToolbar.css";

/**
 * MontageToolbar - Global actions bar for the montage editor
 *
 * Sits between the preview and the timeline.
 * Provides quick access to generation, enhancement, and export actions.
 */
export default function MontageToolbar({
  segments = [],
  saving = false,
  loading = false,
  enhancingPrompts = false,
  generatingImages = false,
  generatingVideos = false,
  imageQuality = "medium",
  videoQuality = "medium",
  onSave,
  onEnhanceAll,
  onGenerateImages,
  onGenerateVideos,
  onAddSegment,
  onSetImageQuality,
  onSetVideoQuality,
  harmonizing = false,
  onHarmonize,
  exporting = false,
  onExport,
}) {
  const segmentCount = segments.length;
  const imagesReady = segments.filter(
    (s) => s.start_image?.status === "completed" && s.end_image?.status === "completed"
  ).length;
  const videosReady = segments.filter(
    (s) => s.video?.status === "completed"
  ).length;

  const allImagesReady = segmentCount > 0 && imagesReady === segmentCount;
  const allVideosReady = segmentCount > 0 && videosReady === segmentCount;

  return (
    <div className="montage-toolbar">
      {/* Left: status chips */}
      <div className="montage-toolbar-left">
        <span className="montage-toolbar-chip">
          <span className="material-symbols-rounded" style={{ fontSize: 16 }}>
            movie
          </span>
          {segmentCount} seg.
        </span>

        <span
          className={`montage-toolbar-chip ${allImagesReady ? "chip-success" : imagesReady > 0 ? "chip-partial" : ""}`}
        >
          <span className="material-symbols-rounded" style={{ fontSize: 16 }}>
            image
          </span>
          {imagesReady}/{segmentCount}
        </span>

        <span
          className={`montage-toolbar-chip ${allVideosReady ? "chip-success" : videosReady > 0 ? "chip-partial" : ""}`}
        >
          <span className="material-symbols-rounded" style={{ fontSize: 16 }}>
            videocam
          </span>
          {videosReady}/{segmentCount}
        </span>
      </div>

      {/* Center: primary actions */}
      <div className="montage-toolbar-center">
        <button
          type="button"
          className="toolbar-btn toolbar-btn-ghost"
          onClick={onAddSegment}
          disabled={loading}
          title={t("montage.toolbar.addSegment")}
        >
          <span className="material-symbols-rounded">add</span>
        </button>

        <button
          type="button"
          className="toolbar-btn toolbar-btn-accent"
          onClick={onEnhanceAll}
          disabled={enhancingPrompts || segmentCount === 0 || loading}
          title={t("montage.toolbar.enhanceAll")}
        >
          <span className="material-symbols-rounded">auto_awesome</span>
          {enhancingPrompts ? "..." : t("montage.toolbar.enhance")}
        </button>

        <div className="toolbar-group">
          <select
            className="toolbar-select"
            value={imageQuality}
            onChange={(e) => onSetImageQuality(e.target.value)}
            disabled={generatingImages}
          >
            <option value="fast">{t("montage.toolbar.fast")}</option>
            <option value="medium">{t("montage.toolbar.medium")}</option>
            <option value="high">{t("montage.toolbar.high")}</option>
          </select>
          <button
            type="button"
            className="toolbar-btn toolbar-btn-primary"
            onClick={onGenerateImages}
            disabled={generatingImages || segmentCount === 0 || loading}
            title={t("montage.toolbar.genImages")}
          >
            <span className="material-symbols-rounded">brush</span>
            {generatingImages ? "..." : t("montage.toolbar.images")}
          </button>
        </div>

        <div className="toolbar-group">
          <select
            className="toolbar-select"
            value={videoQuality}
            onChange={(e) => onSetVideoQuality(e.target.value)}
            disabled={generatingVideos}
          >
            <option value="fast">{t("montage.toolbar.fast")}</option>
            <option value="medium">{t("montage.toolbar.medium")}</option>
            <option value="high">{t("montage.toolbar.high")}</option>
          </select>
          <button
            type="button"
            className="toolbar-btn toolbar-btn-creative"
            onClick={onGenerateVideos}
            disabled={generatingVideos || !allImagesReady || loading}
            title={!allImagesReady ? t("montage.toolbar.genImagesFirst") : t("montage.toolbar.genVideos")}
          >
            <span className="material-symbols-rounded">movie_creation</span>
            {generatingVideos ? "..." : t("montage.toolbar.videos")}
          </button>
        </div>
      </div>

      {/* Right: export + save */}
      <div className="montage-toolbar-right">
        {allVideosReady && (
          <button
            type="button"
            className="toolbar-btn toolbar-btn-accent"
            onClick={onHarmonize}
            disabled={harmonizing || loading}
            title={t("montage.toolbar.harmonizeTitle")}
          >
            <span className="material-symbols-rounded">
              {harmonizing ? "progress_activity" : "palette"}
            </span>
            {harmonizing ? t("montage.toolbar.harmonizing") : t("montage.toolbar.harmonize")}
          </button>
        )}
        {allVideosReady && (
          <button
            type="button"
            className="toolbar-btn toolbar-btn-export"
            onClick={onExport}
            disabled={exporting || loading}
            title={t("montage.toolbar.exportTitle")}
          >
            <span className="material-symbols-rounded">
              {exporting ? "progress_activity" : "movie_creation"}
            </span>
            {exporting ? t("montage.toolbar.exporting") : t("montage.toolbar.export")}
          </button>
        )}
        <button
          type="button"
          className="toolbar-btn toolbar-btn-ghost"
          onClick={onSave}
          disabled={saving}
          title={t("montage.toolbar.save")}
        >
          <span className="material-symbols-rounded">
            {saving ? "hourglass_empty" : "save"}
          </span>
        </button>
      </div>
    </div>
  );
}
